import { EventEmitter, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

import { GPT, GPTIngested, GPTMessage, GPTRequest, GPTResponse, PrivateGPTCompletionRequest, PrivateGPTCompletionRequestMessage, PrivateGPTCompletionResponse } from './gpt.interface';
import { CoreService } from '../../core/service/core.service';
import { CoreUtilities } from '../../core/utilities/core.utilities';
import { GptPrivateGPTService } from './gpt.privategpt.service';
import { CoreFilter, CoreMultiTransfer, TreeNode, TreeRelationship } from '../../core/interface/core.interface';
import { GPTModalComponent } from './modal/gpt.modal';
import {
  NODES_TYPE_AGENT,
  NODES_TYPE_ANSWER,
  NODES_TYPE_CONNECT,
  NODES_TYPE_DATASOURCE, NODES_TYPE_DOCUMENT,
  NODES_TYPE_ENTRY,
  NODES_TYPE_FIELD,
  NODES_TYPE_FIELDS,
  NODES_TYPE_MODEL,
  NODES_TYPE_NODETYPEGROUP,
  NODES_TYPE_ONLY_ONE_STACK,
  NODES_TYPE_PROMPT,
  NODES_TYPE_SKILLS,
  NODES_TYPE_WORKFLOW
} from '../../shared/api/nodes/nodes.models';
import { FormService } from '../../components/form/service/form.service';
import { GptAutogenService } from './gpt.autogen.service';
import { take } from 'rxjs/operators';
import { FormModalComponent } from '../../components/form/modal/form.modal';
import { FormInterface, FormResult } from '../../components/form/interface/form.interface';
import { SubscriptionService } from '../../shared/utilities/subscription';
import { CoreTransformer } from '../../core/transformer/core.transformer';
import { Model } from '../../shared';
import { GptUKnowService } from './gpt.uknow.service';
import { AppGlobal } from '../../app.global';
import { UUID } from 'angular2-uuid';
import { IPayload } from '../payload/payload.interface';
import { Map, Set } from 'immutable';

@Injectable()
export class GPTService {

  /* Core Service */
  public coreService: CoreService;

  /* The GPTs */
  private gpt = {};

  /* Private GPT service */
  private privateGPTService: GptPrivateGPTService;

  /* Autogen service */
  private autogenService: GptAutogenService;

  /* Uknow service */
  private uknowService: GptUKnowService;

  /* Subscription service */
  private subscriptionService = new SubscriptionService();

  public constructor(private coreUtilities: CoreUtilities, private translateService: TranslateService, private formService: FormService, private coreTransformer: CoreTransformer) {}

  public setCoreService(coreService: CoreService) {
    this.coreService = coreService;
    this.privateGPTService = new GptPrivateGPTService(coreService, this.coreUtilities, this.translateService);
    this.autogenService = new GptAutogenService(coreService, this.coreUtilities, this.translateService);
    this.uknowService = new GptUKnowService(coreService, this.coreUtilities, this.translateService);
    this.formService.coreService = coreService;
  }

  /**
   * Open the modal
   *
   * @param {TreeNode[]} selectedTreeNodes
   * @param {TreeNode} configurationNode
   */
  public openModal(selectedTreeNodes: TreeNode[], configurationNode: TreeNode) {
    /* Load modal */
    this.coreService.modal('gpt-modal', (modal: GPTModalComponent) => {
      /* Set the values */
      modal.treeNodes = selectedTreeNodes;
      /* Set the configuration node */
      modal.configurationNode = configurationNode;
      /* Open modal */
      modal.show();
    });
  }

  /**
   * Register a new gpt
   *
   * @param {string} uuid
   * @param {string} type
   * @param {string} url
   * @param ip
   * @param username
   * @param password
   * @param pem
   * @param autogen
   * @param user_id
   * @param async
   * @param alternativeUrl
   * @returns {GPT}
   */
  public register(uuid: string, type: string, url: string, ip?: string, username?: string, password?: string, pem?: string, autogen?: string[], user_id?: string, async?: boolean, alternativeUrl?: string): GPT {

    /* Store the gpt */
    this.gpt[uuid] = { uuid, type, url, ip, username, password, pem, autogen, user_id, async, alternativeUrl } as GPT;

    /* Connect and return event emitter */
    return this.connect(uuid);
  }

  /**
   * Connect to GPT
   *
   * @param {string} uuid
   * @returns {GPT}
   * @private
   */
  private connect(uuid: string): GPT {

    /* Get the gpt */
    const gpt = this.gpt[uuid] as GPT;

    /* The event emitter */
    gpt.events = new BehaviorSubject<GPTResponse>(undefined);

    /* The uuid */
    gpt.uuid = uuid;

    /* Do specific stuff */
    switch (gpt.type) {
      case 'privategpt':
        this.privateGPTService.connect(gpt);
        break;
      case 'autogen':
        this.autogenService.connect(gpt);
        break;
      case 'uknow':
        this.uknowService.connect(gpt);
        break;

    }

    /* Return the emitter */
    return gpt;
  }

  /**
   * Prompt
   *
   * @param gptId
   * @param promptId
   * @param prompt
   * @param conversations
   * @param systemPrompt
   * @param mode
   * @param userDocumentIds
   * @param externalSystems
   * @param autogenWorkflow
   * @param model
   */
  public prompt(gptId: string, promptId: string, prompt: string, conversations: GPTMessage[], systemPrompt?: string, mode?: string, userDocumentIds?: string[], externalSystems?: TreeNode[], autogenWorkflow?: TreeNode, model?: TreeNode) {
    /* Get the gpt */
    const gpt = this.gpt[gptId] as GPT;

    /* Do specific stuff */
    switch (gpt.type) {
      case 'privategpt':
        this.privateGPTService.sendPrompt(gpt, promptId, prompt, conversations, systemPrompt, mode, userDocumentIds, externalSystems, model);
        break;
      case 'autogen':
        this.autogenService.sendPrompt(gpt, promptId, prompt, autogenWorkflow, conversations, systemPrompt);
        break;
      case 'uknow':
          this.uknowService.sendPrompt(gpt, promptId, prompt, conversations, systemPrompt, mode, userDocumentIds, externalSystems, model);
          break;
    }
  }

  /**
   * Close the connection
   *
   */
  public closeConnection() {
    /* CLose private GPT */
    if (this.privateGPTService !== undefined) {
      this.privateGPTService.closeConnection();
    }
  }

  /**
   * Generate system prompt out of nodes
   *
   * @param gptMessage
   * @param {TreeNode[]} treeNodes
   * @param {TreeNode} configurationNode
   * @returns {string}
   */
  public setGPTMessage(gptMessage: GPTMessage, treeNodes: TreeNode[], configurationNode: TreeNode): GPTMessage {
    /* Fields node */
    let fieldsNode: TreeNode;

    const children = configurationNode.children;
    const count = children.length;
    for (let i = 0; i < count; i++) {
      const child = children[i];
      switch (child.nodeType) {
        case NODES_TYPE_FIELDS:
          fieldsNode = child;
          break;
        case NODES_TYPE_DATASOURCE:
          /* Get the model ids */
          const modelIds = this.coreUtilities.unique(treeNodes.map(treeNode => treeNode.modelId));
          /* Set the filters */
          const filters = [{ by: 'nodeType', value: child.children.map(c => c.nodeType) }, { by: 'modelId', value: modelIds }] as CoreFilter[];
          /* Initial tree nodes */
          const initialTreeNodes = this.coreUtilities.flatArray(treeNodes.map(treeNode => treeNode.children)).map(treeNode => treeNode.id);
          /* Get nodes by filters */
          const nodesByFilter = this.coreService.getInstantNodes({ filters, ignoreCache: true, ignoreGlobalFilter: true });
          const count3 = nodesByFilter.length;
          for (let i3 = 0; i3 < count3; i3++) {
            const nodeByFilter = nodesByFilter[i3];
            if (initialTreeNodes.indexOf(nodeByFilter.id) === -1) {
              treeNodes.push(nodeByFilter);
            }
          }
          break;
      }
    }

    if (fieldsNode !== undefined) {
      const fields = [];
      const fieldChildren = fieldsNode.children.sort((a, b) => a.positionX - b.positionX);
      const fieldsCount = fieldChildren.length;
      /* Iterate over the tree nodes */
      for (let i = 0; i < fieldsCount; i++) {
        const fieldChild = fieldChildren[i];
        /* Set field */
        fields.push(fieldChild.formFieldId);
      }
      gptMessage.nodeFields = fields;
      if (treeNodes !== undefined) {
        let modelIds = Set<string>();
        gptMessage.nodeIds = treeNodes.map(treeNode => {
          modelIds = modelIds.add(treeNode.modelId);
          return treeNode.id;
        }).sort((a, b) => a.localeCompare(b));
        gptMessage.modelIds = modelIds.toArray();
      }
    }
    return gptMessage;
  }

  /**
   * Upload files
   *
   * @param {string} gptId
   * @param {FileList} files
   */
  public ingestFile(gptId: string, files: FileList) {
    /* Get the gpt */
    const gpt = this.gpt[gptId] as GPT;

    /* Loading event emitter */
    const eventEmitter = new EventEmitter<boolean>();

    /* Do specific stuff */
    switch (gpt.type) {
      case 'privategpt':
        this.privateGPTService.ingestFile(gpt, files, response => {
          eventEmitter.emit(response);
        });
        break;
    }

    /* Return event emitter */
    return eventEmitter;
  }

  /**
   * Delete ingested file
   *
   * @param {string} gptId
   * @param {GPTIngested} ingestFile
   * @returns {EventEmitter<boolean>}
   */
  public ingestDelete(gptId: string, ingestFile: GPTIngested) {
    /* Get the gpt */
    const gpt = this.gpt[gptId] as GPT;

    /* Loading event emitter */
    const eventEmitter = new EventEmitter<boolean>();

    /* Do specific stuff */
    switch (gpt.type) {
      case 'privategpt':
        eventEmitter.emit();
        break;
      case 'uknow':
        this.uknowService.ingestDelete(gpt, ingestFile, () => {
          /* Delete tree node */
          eventEmitter.emit();
        });
        break;
    }

    /* Return event emitter */
    return eventEmitter;
  }

  /**
   * Get ingest documents
   *
   * @param gpt
   * @param callback
   */
  public getIngestDocuments(gpt: GPT, callback: Function) {
    /* Do specific stuff */
    switch (gpt.type) {
      case 'privategpt':
        this.privateGPTService.getIngestDocuments(gpt, response => {
          callback(response);
        });
        break;
    }
  }

  /**
   * Create something via POST Request
   *
   * @param {GPT} gpt
   * @param treeNode
   * @param {Function} callback
   * @param del
   */
  public createByTreeNode(gpt: GPT, treeNode: TreeNode, callback: Function, del = false) {
    /* Do specific stuff */
    switch (gpt.type) {
      case 'autogen':
        switch (treeNode.nodeType) {
          case NODES_TYPE_SKILLS:
            this.autogenService.createSkill(gpt, treeNode, response => {
              callback(response);
            }, del);
            break;
          case NODES_TYPE_MODEL:
            this.autogenService.createModel(gpt, treeNode, response => {
              callback(response);
            }, del);
            break;
          case NODES_TYPE_AGENT:
            this.autogenService.createAgent(gpt, treeNode, response => {
              callback(response);
            }, del);
            break;
          case NODES_TYPE_WORKFLOW:
            this.autogenService.createWorkflow(gpt, treeNode, response => {
              callback(response);
            }, del);
            break;
        }
        break;
    }
  }

  public getNewSession(gptId: string, model: Model, callback: Function) {

    /* Get the gpt */
    const gpt = this.gpt[gptId] as GPT;

    /* Get the session */
    switch (gpt.type) {
      case 'autogen':
        this.coreService.getModal('form').take(1).subscribe((modal: FormModalComponent) => {

          /* Get the model node type */
          const modelNode = { nodeType: this.coreTransformer.modelTypeToNodeType(model.type), children: [], unfilteredChildren: [] } as TreeNode;

          /* Set the configuration nodes */
          const configuration = [
            { nodeType: NODES_TYPE_NODETYPEGROUP, children: [ { nodeType: NODES_TYPE_WORKFLOW, children: [] } ] },
            { nodeType: NODES_TYPE_MODEL, children: [modelNode], unfilteredChildren: [modelNode] },
            { nodeType: NODES_TYPE_ONLY_ONE_STACK, children: [], unfilteredChildren: [] }
          ];

          /* Set form */
          const form: FormInterface = { tabs: [{ entry: { key: 'header-1', label: '' }, children: this.formService.buildFormEntries({ children: [
                  { name: 'Select workflow', formFieldControlType: 'node-select', unfilteredChildren: configuration, children: configuration } as TreeNode
          ]} as TreeNode)}]};

          /* Register the listeners */
          this.subscriptionService.add('update', modal.update.subscribe((result: FormResult) => {
            const selectedWorkflow = result.delta.first().selected[0];
            if (selectedWorkflow === undefined) {
              this.coreService.toast('Please select a workflow');
            } else {
              modal.isLoading = true;
              this.autogenService.getNewSession(gpt, selectedWorkflow, session => {
                modal.dismiss();
                callback({ 'gpt-session-id': session.id, 'gpt-autogen-workflow': selectedWorkflow.id });
              });
            }
          }));
          /* Show modal */
          modal.show(form, [{ children: [], unfilteredChildren: [], modelId: model.id } as TreeNode]);
        });
        break;
      default:
        callback();
    }
  }

  public checkNodeByLLM(treeNode: TreeNode, formNode: TreeNode, transfer: CoreMultiTransfer, callback: Function) {
    /* Configuration */
    let promptField: string;
    let answerField: string;
    let sourceFields: string[] = [];
    let documentNodeType: number;
    let documentUriField: string;
    let documentPageField: string;
    let documentSnippetField: string;
    let mode: string;
    let sourceNode: TreeNode;
    let connectNode: TreeNode;
    let targetConfigurationNode: TreeNode;

    const children = formNode.children;
    const count = children.length;
    for (let i = 0; i < count; i++) {
      const child = children[i];
      switch (child.nodeType) {
        case NODES_TYPE_PROMPT:
          promptField = child.formFieldId;
          break;
        case NODES_TYPE_ANSWER:
          answerField = child.formFieldId;
          break;
        case NODES_TYPE_FIELDS:
          sourceFields = child.children.map(c => c.formFieldId);
          break;
        case NODES_TYPE_WORKFLOW:
          mode = child.formId;
          break;
        case NODES_TYPE_NODETYPEGROUP:
          sourceNode = child.children[0];
          break;
        case NODES_TYPE_CONNECT:
          connectNode = child.children[0];
          break;
        case NODES_TYPE_ENTRY:
          targetConfigurationNode = child.children[0];
          break;
        case NODES_TYPE_DOCUMENT:
          const documentChildren = child.children;
          const count2 = documentChildren.length;
          for (let i2 = 0; i2 < count2; i2++) {
            const documentChild = documentChildren[i2];
            switch (documentChild.formId) {
              case 'uri':
                documentUriField = documentChild.formFieldId;
                break;
              case 'page':
                documentPageField = documentChild.formFieldId;
                break;
              case 'snippet':
                documentSnippetField = documentChild.formFieldId;
                break;
              case 'nodeType':
                documentNodeType = documentChild.nodeType;
                break;
            }
          }
          break;
      }
    }

    /* Get the nodes by node type */
    const targetNodes = this.coreService.getInstantNodes({ filters: [{ by: 'nodeType', value: targetConfigurationNode.nodeType }, { by: 'modelId', value: transfer.create.modelId }] });
    this.checkTargetNodesByLLM(treeNode, targetNodes, formNode, promptField, answerField, documentNodeType, documentUriField, documentPageField, documentSnippetField, sourceFields, connectNode, transfer, callback);
  }

  protected checkTargetNodesByLLM(treeNode: TreeNode, targetNodes: TreeNode[], formNode: TreeNode, promptField: string, answerField: string, documentNodeType: number, documentUriField: string, documentPageField: string,
                                  documentSnippetField: string, sourceFields: string[], connectNode: TreeNode, transfer: CoreMultiTransfer, callback: Function) {
    if (targetNodes.length === 0) {
      callback(transfer);
      return;
    }
    const targetNode = targetNodes[0];
    /* Get the prompt */
    const prompt = targetNode[promptField];
    if (prompt === '') {
      /* Subtract one element */
      targetNodes.shift();
      /* Rerun */
      this.checkTargetNodesByLLM(treeNode, targetNodes, formNode, promptField, answerField, documentNodeType, documentUriField, documentPageField, documentSnippetField, sourceFields, connectNode, transfer, callback);
      /* Stop */
      return;
    }

    /* Check if node is already there */
    const connectionMap = {};
    if (treeNode.childIds.length > 0) {
      const children = treeNode.children;
      const count = children.length;
      for (let i = 0; i < count; i++) {
        const child = children[i];
        if (child.nodeType === connectNode.nodeType) {
          connectionMap[child.parentIds.join('-')] = child;
        }
      }
    }

    /* Set the system prompt */
    const systemPrompt = this.coreUtilities.unescapeHtml(formNode.description) + ' ' + sourceFields.map(sourceField => treeNode[sourceField]).join(',');
    /* Setup request */
    const request = {
      type: 'privategpt',
      privateGPTCompletionRequest: {
        type: 'chat',
        url: formNode.documentUri,
        messages: [
          { role: 'system', content: systemPrompt } as PrivateGPTCompletionRequestMessage,
          { role: 'user', content: prompt } as PrivateGPTCompletionRequestMessage
        ],
        use_context: true,
        include_sources: true
      } as PrivateGPTCompletionRequest
    } as GPTRequest;
    /* Do the prompt */
    this.coreService.gpt('completion', request).take(1).subscribe((response: PrivateGPTCompletionResponse) => {
      /* Answer */
      const message = response.choices[0].message.content;

      /* Create element */
      let update = false;
      let element = { id: UUID.UUID(), nodeType: connectNode.nodeType, name: treeNode.name + '-' + targetNode.name } as TreeNode;
      if (connectionMap[treeNode.id + '-' + targetNode.id] !== undefined) {
        update = true;
        element = connectionMap[treeNode.id + '-' + targetNode.id];
      }

      if (!update) {
        element[answerField] = message;
        (transfer.create.nodes as TreeNode[]).push(element);
      } else {
        /* Update */
        (transfer.update.nodes as IPayload[]).push({ id: element.dataId, data: Map().set(answerField, message) } as IPayload);

        /* Remove existing documents */
        (transfer.delete.nodes as TreeNode[]) = (transfer.delete.nodes as TreeNode[]).concat(element.children.filter(child => child.nodeType === documentNodeType));
      }

      /* Check for documents */
      const count = response.choices[0].sources.length;
      for (let i = 0; i < count; i++) {
        const source = response.choices[0].sources[i];
        const document = { id: UUID.UUID(), nodeType: documentNodeType, name: source.document.doc_metadata.file_name } as TreeNode;
        document[documentUriField] = source.document.doc_metadata.file_name;
        document[documentPageField] = source.document.doc_metadata.page_label;
        document[documentSnippetField] = source.text;
        (transfer.create.nodes as TreeNode[]).push(document);
        (transfer.create.relationships as TreeRelationship[]).push({ id: UUID.UUID(), parentId: element.id, childId: document.id } as TreeRelationship);
      }

      /* Connect element */
      (transfer.create.relationships as TreeRelationship[]).push({ id: UUID.UUID(), parentId: treeNode.id, childId: element.id } as TreeRelationship);
      (transfer.create.relationships as TreeRelationship[]).push({ id: UUID.UUID(), parentId: targetNode.id, childId: element.id } as TreeRelationship);

      /* Subtract one element */
      targetNodes.shift();
      /* Rerun */
      this.checkTargetNodesByLLM(treeNode, targetNodes, formNode, promptField, answerField, documentNodeType, documentUriField, documentPageField, documentSnippetField, sourceFields, connectNode, transfer, callback);
    });
  }

}
